import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { TIERS } from '../../constants/pricingConfig';

const TierCard = ({ tier }) => {
  const navigate = useNavigate();
  const isEnterprise = tier.id === 'enterprise';

  const handleCTA = () => {
    if (isEnterprise) {
      window.location.href = 'mailto:sales@craftflow.com?subject=Enterprise%20Inquiry';
    } else {
      navigate(`/login?tab=register&plan=${tier.id}`);
    }
  };

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-white p-8 transition-all duration-200 ${
        tier.highlight
          ? 'border-lavender-400 shadow-xl shadow-lavender-100/50 ring-1 ring-lavender-400 scale-[1.02] z-10'
          : 'border-gray-200 shadow-xs hover:shadow-md hover:border-gray-300'
      }`}
    >
      {tier.highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-gradient-to-r from-lavender-500 to-lavender-600 text-white text-xs font-semibold uppercase tracking-wide shadow-md">
            <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" />
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900">{tier.name}</h3>
        <p className="mt-1 text-sm text-gray-500 leading-relaxed min-h-[40px]">{tier.description}</p>
      </div>

      <div className="mb-8">
        {isEnterprise ? (
          <span className="text-4xl font-extrabold text-gray-900">Custom</span>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-gray-900">${tier.price}</span>
              <span className="text-sm text-gray-500 font-medium">/ month</span>
            </div>
            <p className="mt-1 text-xs text-gray-400">${tier.yearlyPrice}/mo billed annually</p>
          </>
        )}
      </div>

      <button
        onClick={handleCTA}
        className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
          tier.highlight
            ? 'bg-gradient-to-r from-lavender-500 to-lavender-600 text-white hover:from-lavender-600 hover:to-lavender-700 shadow-md shadow-lavender-200/50'
            : isEnterprise
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
        }`}
      >
        {tier.ctaLabel}
      </button>

      <ul className="mt-8 space-y-3 flex-1">
        {tier.features.map(feature => (
          <li key={feature} className="flex items-start gap-3">
            <CheckIcon
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-lavender-600' : 'text-gray-400'}`}
              aria-hidden="true"
            />
            <span className="text-sm text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PricingSection = () => {
  return (
    <section id="pricing" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Pricing that scales with your business
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            From your first Etsy listing to a full multi-channel operation. Pick the plan that matches where you are
            today — upgrade anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 xl:gap-6 max-w-7xl mx-auto items-start">
          {TIERS.map(tier => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
